import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  ListItem,
  UnorderedList,
  VStack
} from "@chakra-ui/react";
import { StyledInputField } from "@root/commonStyles/commonStyle";
import { CustonButtom } from "@root/components/common/button/customButton";
import { CustomText } from "@root/components/common/components";
import { REQUIREMENTS } from "@root/constants/common";
import { PROFILE_MESSAGES } from "@root/constants/profile";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { userPasswordChange } from "@root/store/thunks";
import { Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { UserProfileContainer } from "./styles";

export const UserPassword: React.FC = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  const currentUser: allAnyTypes = useSelector(getLoggedInUser);
  const [showOldPassword, setShowOldPassword] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(true);

  const initialValues = {
    email: currentUser?.user?.email || "",
    fullname: currentUser?.user?.fullname || "",
    apiKey: currentUser?.API_Key || "",
    old_password: "",
    new_password: "",
  };

  const PasswordSchema = Yup.object().shape({
    old_password: Yup.string().required(t(`${PROFILE_MESSAGES.OLD_PASSWORD}`)),
    new_password: Yup.string()
      .required(t(`${PROFILE_MESSAGES.NEW_PASSWORD}`))
      .min(6, t("password.passwordLength"))
      .matches(/[A-Z]/, t("password.containLetter"))
      .matches(/[a-z]/, t("password.containlowerCase"))
      .matches(/[0-9]/, t("password.containNumber"))
      .matches(/[!@#$%^&*(),.?":{}|<>]/, t("password.containNumber")),
  });

  const handleOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleSubmit = (values: any) => {
    const { old_password, new_password } = values;
    const body = { old_password, new_password };
    dispatch(userPasswordChange({ payload: body }));
  };

  return (
    <UserProfileContainer>
      <CustomText fontSize={"20px"} fontWeight={500} mb={4} text={"Password"} />
      <FormLabel color={"gray.500"} marginTop={"20px"} marginBottom={"10px"}>
        {t(`${PROFILE_MESSAGES.MANAGE_PASSWORD}`)}
      </FormLabel>
      <Formik
        initialValues={initialValues}
        validationSchema={PasswordSchema}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({ handleSubmit, errors, touched, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <VStack
              mt="28px"
              spacing={"16px"}
              align="flex-start"
              border={"1px"}
              borderColor={"border.inverseGray"}
              borderRadius={"16px"}
              padding={"20px"}
              width={{ base: "100%", md: "860px" }}
              backgroundColor={"inverse.gray"}
              bg={"theme.bg.box"}
            >
              <FormLabel fontSize={"18px"} color={"inverse.black"}>
                {t("password.password")}
              </FormLabel>
              <FormLabel htmlFor="oldPassword">
                {t("password.oldPassword")}
              </FormLabel>
              <FormControl
                isInvalid={!!errors.old_password && touched.old_password}
              >
                <InputGroup>
                  <StyledInputField
                    onChange={(e: any) =>
                      setFieldValue("old_password", e.target.value)
                    }
                    borderColor={
                      values.old_password ? "theme.gray.200" : "theme.gray.200"
                    }
                    as={Input}
                    id="old_password"
                    name="old_password"
                    type={showOldPassword ? "password" : "text"}
                    required
                    variant="filled"
                    placeholder={t(`${PROFILE_MESSAGES.OLD_PASSWORD}`)}
                  />
                  <InputRightElement >
                    {showOldPassword ? (
                      <ViewOffIcon
                        cursor={'pointer'}
                        boxSize={4}
                        color="gray.300"
                        onClick={handleOldPassword}
                      />
                    ) : (
                      <ViewIcon
                        cursor={'pointer'}
                        boxSize={4}
                        color="gray.300"
                        onClick={handleOldPassword}
                      />
                    )}
                  </InputRightElement>
                </InputGroup>
                {errors.old_password && touched.old_password && (
                  <FormLabel color="red.500">{errors.old_password}</FormLabel>
                )}
              </FormControl>
              <FormLabel htmlFor="newPassword">
                {t("password.newPassword")}
              </FormLabel>
              <FormControl
                isInvalid={!!errors.new_password && touched.new_password}
              >
                <InputGroup>
                  <StyledInputField
                    value={values.new_password}
                    onChange={(e: any) =>
                      setFieldValue("new_password", e.target.value)
                    }
                    borderColor={
                      values.new_password ? "primary.200" : "gray.200"
                    }
                    as={Input}
                    id="new_password"
                    name="new_password"
                    type={showNewPassword ? "password" : "text"}
                    required
                    variant="filled"
                    placeholder={t(`${PROFILE_MESSAGES.NEW_PASSWORD}`)}
                  />
                  <InputRightElement>
                    {showNewPassword ? (
                      <ViewOffIcon
                      cursor={'pointer'}
                        boxSize={4}
                        color="gray.300"
                        onClick={handleNewPassword}
                      />
                    ) : (
                      <ViewIcon
                      cursor={'pointer'}
                        boxSize={4}
                        color="gray.300"
                        onClick={handleNewPassword}
                      />
                    )}
                  </InputRightElement>
                </InputGroup>
                {errors.new_password && touched.new_password && (
                  <FormLabel color="red.500">{errors.new_password}</FormLabel>
                )}
              </FormControl>

              <Flex
                gap={"16px"}
                flexWrap={'wrap'}
                justifyContent="space-between"
                width="full"
              >
                <div>
                  <FormLabel color={"inverse.black"}>
                    {t("password.requirements")}
                  </FormLabel>
                  <UnorderedList>
                    {REQUIREMENTS.map((item, index) => (
                      <ListItem color={"inverse.black"} key={index}>
                        {t(`${item}`)}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </div>
                <CustonButtom onClick={handleSubmit} text={t("password.saveChanges")} />
              </Flex>
            </VStack>
          </form>
        )}
      </Formik>
    </UserProfileContainer>
  );
};
